import Stack from '@mui/material/Stack';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useDebounce } from 'usehooks-ts';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { get } from '../../Shared/ajax';
import { useSnackbar } from 'notistack';
import { SxProps, Theme } from '@mui/material/styles';

interface ComponentProps {
  open?: boolean;
  sx?: SxProps<Theme>;
  url: string;
  label: string;
  isDisabled?: boolean;
  onInputChange?: (value: string | null) => void;
  onSelectedOption?: (value: string) => void;
  onOpen?: (e: SyntheticEvent<Element, Event>) => void;
}

export default ({
  open,
  url,
  sx,
  label,
  isDisabled,
  onInputChange,
  onSelectedOption,
  onOpen
}: ComponentProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [inputAutocomplete, setInputAutocomplete] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [eventDataOptions, setEventDataOptions] = useState<string[]>([]);

  const debouncedSearchTerm = useDebounce<string>(inputAutocomplete, 300);

  const wrapperEl = useRef<HTMLDivElement>(null);

  const handleSelectedOptionAutocomplete = (
    event: SyntheticEvent<Element, Event>,
    value: string | null
  ) => {
    if (onSelectedOption) {
      onSelectedOption(value as string);
    }
  };

  const handleInputChangeAutocomplete = (
    event: SyntheticEvent<Element, Event>,
    input: string
  ) => {
    setInputAutocomplete(input);
    if (onInputChange) {
      onInputChange(input);
    }
  };

  const fetchEventDataOptions = async (searchTerm: string, url: string) => {
    if (!searchTerm) return;

    try {
      const formattedUrl = url.includes('?')
        ? `${url}&searchTerm=${searchTerm}`
        : `${url}?searchTerm=${searchTerm}`;

      setIsLoading(true);

      const response = await get<string[]>(formattedUrl);

      setIsLoading(false);

      setEventDataOptions(response || []);
    } catch (err) {
      if (typeof err === 'string') {
        enqueueSnackbar(err, { variant: 'error' });
      } else {
        enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
      }
      setIsLoading(false);
      return;
    }
  };

  useEffect(() => {
    fetchEventDataOptions(debouncedSearchTerm, url);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (open) {
      wrapperEl?.current?.querySelector('input')?.focus();
    } else {
      setInputAutocomplete('');
    }
  }, [open]);

  return (
    <Stack
      ref={wrapperEl}
      sx={{
        ...sx,
        backgroundColor: '#FFFFFF'
      }}
    >
      <Autocomplete
        onChange={handleSelectedOptionAutocomplete}
        onInputChange={handleInputChangeAutocomplete}
        onOpen={onOpen}
        inputValue={inputAutocomplete}
        loading={isLoading}
        options={eventDataOptions}
        noOptionsText={
          !debouncedSearchTerm && !eventDataOptions.length
            ? 'Start typing to retrieve options'
            : 'No options found'
        }
        open={!isDisabled && open}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              disabled={isDisabled}
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon />,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          );
        }}
        disabled={isDisabled}
      />
    </Stack>
  );
};
